import { readToken } from '../lib/screen-token'

/**
 * Клиент API трекера. База — VITE_API_BASE_URL (на проде это api.8ballpos.by),
 * иначе /api того же origin: так же, как в apps/booking.
 */
const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? `${location.origin}/api`

/** Ошибка запроса с HTTP-статусом: по 401 экран уходит на привязку. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message)
  }
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
  opts: { auth?: boolean; signal?: AbortSignal } = {}
): Promise<T> {
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (opts.auth !== false) {
    const token = readToken()
    if (token) headers.Authorization = `Bearer ${token}`
  }
  const res = await fetch(`${BASE}/${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: opts.signal
  })
  const text = await res.text()
  const json = text ? (JSON.parse(text) as unknown) : null
  if (!res.ok) {
    const msg =
      (json as { message?: string | string[] } | null)?.message ?? `Ошибка запроса (${res.status})`
    throw new ApiError(Array.isArray(msg) ? msg.join(', ') : msg, res.status)
  }
  return json as T
}

/** Тикет экрана — форма совпадает с терминальным KitchenTicket. */
export interface Ticket {
  id: number
  orderId: number
  name: string
  /** Объём/порция («3 л»). Старая касса поля не шлёт — тогда undefined. */
  portion?: string | null
  qty: number
  tableLabel: string | null
  section: 'kitchen' | 'bar'
  status: 'queued' | 'cooking' | 'ready' | 'served'
  orderFulfillment: string
  prepTimeMinutes: number | null
  createdAt: number
}

export interface Board {
  rev: number
  online: boolean
  updatedAt: number | null
  clubName: string
  deviceName: string
  section: 'kitchen' | 'bar'
  deliveryEnabled: boolean
  tickets: Ticket[] | null
}

export interface PairResult {
  token: string
  deviceName: string
  section: 'kitchen' | 'bar'
  deliveryEnabled: boolean
  clubName: string
}

export const trackerApi = {
  pair: (pairCode: string) =>
    request<PairResult>('POST', 'v1/tracker/pair', { pairCode }, { auth: false }),

  /** Long-poll доски: сервер держит ответ, пока не изменится ревизия. */
  board: (rev: number, signal?: AbortSignal) =>
    request<Board>('GET', `v1/tracker/tickets?rev=${rev}&waitMs=20000`, undefined, { signal }),

  setStatus: (ticketId: number, status: Ticket['status']) =>
    request<{ commandId: string }>('POST', `v1/tracker/tickets/${ticketId}/status`, { status }),

  deliver: (orderId: number) =>
    request<{ commandId: string }>('POST', `v1/tracker/orders/${orderId}/delivery`),

  unpair: () => request<{ ok: true }>('POST', 'v1/tracker/unpair')
}
